import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { socialAPI } from '../../utils/api';

// Async thunk для лайка контента
export const likeContent = createAsyncThunk(
  'likes/likeContent',
  async ({ contentType, contentId }, { rejectWithValue }) => {
    try {
      const response = await socialAPI.likeContent(contentType, contentId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to like content');
    }
  }
);

// Async thunk для удаления лайка
export const unlikeContent = createAsyncThunk(
  'likes/unlikeContent',
  async ({ contentType, contentId }, { rejectWithValue }) => {
    try {
      const response = await socialAPI.unlikeContent(contentType, contentId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to unlike content');
    }
  }
);

// Async thunk для получения лайков контента
export const fetchContentLikes = createAsyncThunk(
  'likes/fetchContentLikes',
  async ({ contentType, contentId, limit = 20, offset = 0 }, { rejectWithValue }) => {
    try {
      const response = await socialAPI.getLikes(contentType, contentId, { limit, offset });
      // Извлекаем данные из ответа
      const likesData = response.data;
      const count = likesData?.count || 0;
      const likes = Array.isArray(likesData?.likes) ? likesData.likes : [];
      const hasMore = likesData?.has_more || false;
      const is_liked = likesData?.is_liked !== undefined ? likesData.is_liked : undefined;
      
      return { contentType, contentId, count, likes, hasMore, is_liked };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch likes');
    }
  }
);

// Async thunk для батч-загрузки лайков (для страницы карты — один запрос вместо N)
export const fetchContentLikesBatch = createAsyncThunk(
  'likes/fetchContentLikesBatch',
  async ({ contentType, contentIds }, { rejectWithValue }) => {
    try {
      const response = await socialAPI.getLikesBatch(contentType, contentIds);
      const data = response?.data || {};
      return { contentType, data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch likes batch');
    }
  }
);

// Async thunk для получения лайков пользователя
export const fetchUserLikes = createAsyncThunk(
  'likes/fetchUserLikes',
  async ({ userId, limit = 20, offset = 0 }, { rejectWithValue }) => {
    try {
      const response = await socialAPI.getUserLikes(userId, { limit, offset });
      return { userId, likes: response.data, hasMore: response.data.length === limit };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch user likes');
    }
  }
);

const likesSlice = createSlice({
  name: 'likes',
  initialState: {
    // Статус лайков для контента: { 'contentType_contentId': { isLiked: boolean, count: number, isLoading: boolean } }
    contentLikes: {},
    // Список лайков для контента: { 'contentType_contentId': { users: [], hasMore: boolean, isLoading: boolean } }
    contentLikesList: {},
    // Лайки пользователя: { userId: { likes: [], hasMore: boolean, isLoading: boolean } }
    userLikes: {},
    // Общее состояние
    isLoading: false,
    error: null,
  },
  reducers: {
    // Обновление статуса лайка (приходит через WebSocket)
    updateLikeStatus: (state, action) => {
      const { contentType, contentId, isLiked, count } = action.payload;
      const key = `${contentType}_${contentId}`;
      
      if (!state.contentLikes[key]) {
        state.contentLikes[key] = { isLiked: false, count: 0, isLoading: false };
      }
      
      state.contentLikes[key].isLiked = isLiked;
      state.contentLikes[key].count = count;
    },

    // Добавление нового лайка (приходит через WebSocket)
    addLike: (state, action) => {
      const { contentType, contentId, user } = action.payload;
      const key = `${contentType}_${contentId}`;
      
      // Обновляем счетчик
      if (!state.contentLikes[key]) {
        state.contentLikes[key] = { isLiked: false, count: 0, isLoading: false };
      }
      state.contentLikes[key].count += 1;
      
      // Добавляем в список лайков
      if (!state.contentLikesList[key]) {
        state.contentLikesList[key] = { users: [], hasMore: true, isLoading: false };
      }
      
      // Проверяем, нет ли уже такого лайка
      const exists = state.contentLikesList[key].users.find(u => u.id === user.id);
      if (!exists) {
        state.contentLikesList[key].users.unshift(user);
      }
    },

    // Удаление лайка (приходит через WebSocket)
    removeLike: (state, action) => {
      const { contentType, contentId, userId } = action.payload;
      const key = `${contentType}_${contentId}`;
      
      // Обновляем счетчик
      if (state.contentLikes[key]) {
        state.contentLikes[key].count = Math.max(0, state.contentLikes[key].count - 1);
      }
      
      // Удаляем из списка лайков
      if (state.contentLikesList[key]) {
        state.contentLikesList[key].users = state.contentLikesList[key].users.filter(
          u => u.id !== userId
        );
      }
    },

    // Оптимистичное обновление лайка
    toggleLikeOptimistic: (state, action) => {
      const { contentType, contentId, isLiked, count } = action.payload;
      const key = `${contentType}_${contentId}`;
      
      if (!state.contentLikes[key]) {
        state.contentLikes[key] = { isLiked: false, count: 0, isLoading: false };
      }
      
      const wasLiked = state.contentLikes[key].isLiked;
      state.contentLikes[key].isLiked = isLiked;
      
      // Обновляем счетчик (если передан в payload, используем его, иначе вычисляем)
      if (count !== undefined) {
        state.contentLikes[key].count = count;
      } else {
        // Автоматическое вычисление счетчика
        if (isLiked && !wasLiked) {
          state.contentLikes[key].count += 1;
        } else if (!isLiked && wasLiked) {
          state.contentLikes[key].count = Math.max(0, state.contentLikes[key].count - 1);
        }
      }
    },

    // Очистка ошибки
    clearError: (state) => {
      state.error = null;
    },

    // Сброс состояния
    resetLikesState: (state) => {
      state.contentLikes = {};
      state.contentLikesList = {};
      state.userLikes = {};
      state.isLoading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Лайк контента
      .addCase(likeContent.pending, (state, action) => {
        const { contentType, contentId } = action.meta.arg;
        const key = `${contentType}_${contentId}`;
        
        if (!state.contentLikes[key]) {
          state.contentLikes[key] = { isLiked: false, count: 0, isLoading: false };
        }
        state.contentLikes[key].isLoading = true;
        state.error = null;
      })
      .addCase(likeContent.fulfilled, (state, action) => {
        const { contentType, contentId } = action.meta.arg;
        const key = `${contentType}_${contentId}`;
        
        if (!state.contentLikes[key]) {
          state.contentLikes[key] = { isLiked: false, count: 0, isLoading: false };
        }
        
        // Обновляем из ответа API (если есть)
        if (action.payload) {
          const newIsLiked = action.payload.liked !== undefined ? action.payload.liked : true;
          const newCount = action.payload.count !== undefined ? action.payload.count : state.contentLikes[key].count;
          
          state.contentLikes[key].isLiked = newIsLiked;
          state.contentLikes[key].count = newCount;
          
          // Если лайкаем видео, нужно также обновить статус маршрута (если видео привязано к маршруту)
          // Это делается через fetchContentLikes в LikeButton, но для надежности обновим здесь тоже
        } else {
          state.contentLikes[key].isLiked = true;
        }
        state.contentLikes[key].isLoading = false;
      })
      .addCase(likeContent.rejected, (state, action) => {
        const { contentType, contentId } = action.meta.arg;
        const key = `${contentType}_${contentId}`;
        
        if (state.contentLikes[key]) {
          state.contentLikes[key].isLoading = false;
        }
        state.error = action.payload;
      })
      
      // Удаление лайка
      .addCase(unlikeContent.pending, (state, action) => {
        const { contentType, contentId } = action.meta.arg;
        const key = `${contentType}_${contentId}`;
        
        if (!state.contentLikes[key]) {
          state.contentLikes[key] = { isLiked: false, count: 0, isLoading: false };
        }
        state.contentLikes[key].isLoading = true;
        state.error = null;
      })
      .addCase(unlikeContent.fulfilled, (state, action) => {
        const { contentType, contentId } = action.meta.arg;
        const key = `${contentType}_${contentId}`;
        
        if (!state.contentLikes[key]) {
          state.contentLikes[key] = { isLiked: false, count: 0, isLoading: false };
        }
        
        // Обновляем из ответа API (если есть)
        if (action.payload) {
          const newIsLiked = action.payload.liked !== undefined ? action.payload.liked : false;
          const newCount = action.payload.count !== undefined ? action.payload.count : state.contentLikes[key].count;
          
          state.contentLikes[key].isLiked = newIsLiked;
          state.contentLikes[key].count = newCount;
          
          // Если убираем лайк с видео, нужно также обновить статус маршрута
          // Это делается через fetchContentLikes в LikeButton, но для надежности обновим здесь тоже
        } else {
          state.contentLikes[key].isLiked = false;
        }
        state.contentLikes[key].isLoading = false;
      })
      .addCase(unlikeContent.rejected, (state, action) => {
        const { contentType, contentId } = action.meta.arg;
        const key = `${contentType}_${contentId}`;
        
        if (state.contentLikes[key]) {
          state.contentLikes[key].isLoading = false;
        }
        state.error = action.payload;
      })
      
      // Получение лайков контента
      .addCase(fetchContentLikes.pending, (state, action) => {
        const { contentType, contentId } = action.meta.arg;
        const key = `${contentType}_${contentId}`;
        
        if (!state.contentLikesList[key]) {
          state.contentLikesList[key] = { users: [], hasMore: true, isLoading: false };
        }
        state.contentLikesList[key].isLoading = true;
      })
      .addCase(fetchContentLikes.fulfilled, (state, action) => {
        const { contentType, contentId, count, likes, hasMore, is_liked } = action.payload;
        const key = `${contentType}_${contentId}`;
        
        // Обновляем количество лайков и статус
        if (!state.contentLikes[key]) {
          state.contentLikes[key] = { isLiked: false, count: 0, isLoading: false };
        }
        state.contentLikes[key].count = count || 0;
        // ВСЕГДА обновляем статус лайка из ответа (даже если undefined, чтобы сбросить старое значение)
        state.contentLikes[key].isLiked = is_liked !== undefined ? is_liked : false;
        state.contentLikes[key].isLoading = false;
        
        // Обновляем список лайков
        if (!state.contentLikesList[key]) {
          state.contentLikesList[key] = { users: [], hasMore: true, isLoading: false };
        }
        state.contentLikesList[key].users = likes || [];
        state.contentLikesList[key].hasMore = hasMore !== undefined ? hasMore : true;
        state.contentLikesList[key].isLoading = false;
      })
      .addCase(fetchContentLikes.rejected, (state, action) => {
        const { contentType, contentId } = action.meta.arg;
        const key = `${contentType}_${contentId}`;
        
        if (state.contentLikesList[key]) {
          state.contentLikesList[key].isLoading = false;
        }
        state.error = action.payload;
      })
      // Батч лайков (страница карты)
      .addCase(fetchContentLikesBatch.fulfilled, (state, action) => {
        const { contentType, data } = action.payload;
        if (!data || typeof data !== 'object') return;
        Object.keys(data).forEach((contentIdStr) => {
          const key = `${contentType}_${contentIdStr}`;
          const item = data[contentIdStr];
          if (!state.contentLikes[key]) {
            state.contentLikes[key] = { isLiked: false, count: 0, isLoading: false };
          }
          state.contentLikes[key].count = item?.count ?? 0;
          state.contentLikes[key].isLiked = item?.is_liked === true;
        });
      })
      
      // Получение лайков пользователя
      .addCase(fetchUserLikes.pending, (state, action) => {
        const { userId } = action.meta.arg;
        
        if (!state.userLikes[userId]) {
          state.userLikes[userId] = { likes: [], hasMore: true, isLoading: false };
        }
        state.userLikes[userId].isLoading = true;
      })
      .addCase(fetchUserLikes.fulfilled, (state, action) => {
        const { userId, likes, hasMore } = action.payload;
        
        state.userLikes[userId] = { likes, hasMore, isLoading: false };
      })
      .addCase(fetchUserLikes.rejected, (state, action) => {
        const { userId } = action.meta.arg;
        
        if (state.userLikes[userId]) {
          state.userLikes[userId].isLoading = false;
        }
        state.error = action.payload;
      });
  },
});

// Экспортируем actions
export const {
  updateLikeStatus,
  addLike,
  removeLike,
  toggleLikeOptimistic,
  clearError,
  resetLikesState,
} = likesSlice.actions;

// Селекторы
export const selectContentLikeStatus = (state, contentType, contentId) => {
  const key = `${contentType}_${contentId}`;
  return state.likes.contentLikes[key] || { isLiked: false, count: 0, isLoading: false };
};
export const selectContentLikesList = (state, contentType, contentId) => {
  const key = `${contentType}_${contentId}`;
  return state.likes.contentLikesList[key] || { users: [], hasMore: true, isLoading: false };
};
export const selectUserLikes = (state, userId) => {
  return state.likes.userLikes[userId] || { likes: [], hasMore: true, isLoading: false };
};
export const selectLikesLoading = (state) => state.likes.isLoading;
export const selectLikesError = (state) => state.likes.error;

export default likesSlice.reducer;
